import { useApiQuery, useApiMutation } from "lib/react-query";

// Financial
export const useFinancialTrees = () => {
  return useApiQuery("dashboard/financial-trees");
};

export const useFinancialTreesSearch = (queryOptions = {}) => {
  return useApiQuery(
    "dashboard/financial-trees-search",
    null,
    false,
    null,
    {},
    queryOptions
  );
};

// Taxes
/**
 * @param {string} [search] - Search query string.
 * @param {boolean} [includeDeleted=true] - When false, appends `deleted=false` to filter
 *   out soft-deleted records. Defaults to true to preserve backward compatibility with
 *   existing call sites. Pass `!!id` in add/edit forms so edit mode shows previously
 *   selected deleted items while create mode hides them.
 */
export const useTaxesSearch = (
  search,
  includeDeleted = true,
  active = true
) => {
  const deletedParam = includeDeleted ? "" : "&deleted=false";
  const activeParam = active ? "&active=true" : "";
  return useApiQuery(
    `dashboard/taxes/search?search=${encodeURIComponent(search ?? "")}${deletedParam}${activeParam}`
  );
};

// Customers
export const useCustomersSearch = (search) => {
  return useApiQuery(
    `dashboard/customers-search?search=${encodeURIComponent(search ?? "")}&deleted=false`
  );
};

export const useCustomersSearchWithOptions = (search, { id, deleted } = {}) => {
  const params = new URLSearchParams();
  params.set("search", search ?? "");
  if (id) params.set("id", id);
  if (!id) params.set("deleted", String(deleted ?? false));
  return useApiQuery(`dashboard/customers-search?${params.toString()}`);
};

// Entities
export const useEntitiesSearch = (deleted = false) => {
  return useApiQuery(`dashboard/entities-search?deleted=${deleted}`);
};

// Units
export const useUnitsSearch = (name) => {
  return useApiQuery(
    `dashboard/units-search?search=${encodeURIComponent(name ?? "")}&deleted=false`
  );
};

export const useUnitsSearchWithDeleted = (search, deleted = false) => {
  return useApiQuery(
    `dashboard/units-search?search=${encodeURIComponent(search ?? "")}&deleted=${deleted}`
  );
};

// Projects
export const useProjectsSearch = (search, deleted = false) => {
  return useApiQuery(
    `dashboard/projects-search?search=${encodeURIComponent(search ?? "")}&deleted=${deleted}`
  );
};

// Properties (search)
export const usePropertiesSearch = (search, deleted = false) => {
  return useApiQuery(
    `dashboard/properties-search?search=${encodeURIComponent(search ?? "")}&deleted=${deleted}`
  );
};

// Properties
/**
 * Every property type the office holds. `per_page` is named because the endpoint
 * defaults to 15 and the catalog is past half of that — every caller here is a
 * picker or a grid of the whole list, so a second page it never asks for would
 * read as a type that does not exist.
 */
export const usePropertyTypes = (enabled = true, queryOptions = {}) => {
  return useApiQuery(
    enabled ? "dashboard/property-types?per_page=100" : null,
    null,
    false,
    null,
    {},
    queryOptions
  );
};

/**
 * Unit (child) types configured on property types — a residential building holds both
 * apartments and shops, so unit-side pickers list these instead of property types.
 *
 * @param {boolean} [enabled=true] - Skip the request while false (e.g. modal closed).
 * @param {{ propertyTypeId?: number|string, actAsProperty?: boolean }} [filters]
 */
export const usePropertyChildTypes = (
  enabled = true,
  filters = {},
  queryOptions = {}
) => {
  const params = new URLSearchParams({ per_page: "100" });
  if (filters.propertyTypeId) {
    params.set("property_type_id", filters.propertyTypeId);
  }
  if (filters.actAsProperty != null) {
    params.set("act_as_property", filters.actAsProperty ? "1" : "0");
  }

  return useApiQuery(
    enabled ? `dashboard/property-child-types?${params.toString()}` : null,
    null,
    false,
    null,
    {},
    queryOptions
  );
};

// Cities & Districts
export const useCities = (countryCode = "SAU", noPage = true) => {
  return useApiQuery(
    countryCode ? `cities/${countryCode}${noPage ? "?no_page=1" : ""}` : null
  );
};

export const useDistricts = (cityId, noPage = true) => {
  return useApiQuery(
    cityId ? `districts/${cityId}${noPage ? "?no_page=1" : ""}` : null
  );
};

// Countries
export const useCountries = () => {
  return useApiQuery("countries");
};

// Banks
export const useCountriesBanks = (search, queryOptions = {}) => {
  return useApiQuery(
    search
      ? `countries-banks?search=${encodeURIComponent(search)}`
      : "countries-banks",
    null,
    false,
    null,
    {},
    queryOptions
  );
};

// Warehouses
export const useWarehousesSearch = () => {
  return useApiQuery("dashboard/warehouses-search?deleted=false");
};

// Inventories
export const useInventoriesSearch = () => {
  return useApiQuery("dashboard/inventories-search?deleted=false");
};

// Services
export const useServicesSearch = () => {
  return useApiQuery("dashboard/services-search?search=");
};

// Contracts search
export const useContractsSearch = (name) => {
  return useApiQuery(
    `dashboard/contracts?name=${encodeURIComponent(name ?? "")}&deleted=false`
  );
};

export const useSaleContractsSearch = (name) => {
  return useApiQuery(
    `dashboard/sell-contracts?name=${encodeURIComponent(name ?? "")}&deleted=false`
  );
};

/**
 * A property type's custom fields, grouped by category.
 *
 * `searchable` narrows the result to the fields the tenant marked as search criteria
 * (`custom_fields.searchable = 1`). Two different questions share this endpoint:
 *
 * - Describing one unit wants **every** field — that is what the unit's own record is made of.
 * - Stating what a lead is *looking for* wants only the searchable ones, because a lead
 *   preference is fed straight into the unit matcher. A non-searchable field there still adds
 *   a `WHERE` to the match (`UnitRepository::applyCustomFieldsFilter` filters on any field id
 *   it is handed), so it narrows the matched units by a criterion the tenant said should not
 *   be searched on.
 *
 * Defaults to the full list, so existing callers are unchanged.
 */
export const useCustomFields = (propertyTypeId, searchable = false) => {
  return useApiQuery(
    propertyTypeId
      ? `dashboard/custom-fields/${propertyTypeId}${searchable ? "?searchable=1" : ""}`
      : null
  );
};

// Admins
export const useAdminsSearch = (query) => {
  return useApiQuery(`dashboard/admins-search`, null, false, query);
};

// Inventory categories
/**
 * @param {boolean} [includeDeleted=true] - When false, appends `?deleted=false` to filter
 *   out soft-deleted categories. Defaults to true to preserve backward compatibility with
 *   existing call sites. Pass `!!id` in add/edit forms so edit mode shows previously
 *   selected deleted categories while create mode hides them.
 */
export const useInventoryCategoriesParents = (includeDeleted = true) => {
  const deletedParam = includeDeleted ? "" : "?deleted=false";
  return useApiQuery(`dashboard/inventory-categories/parents${deletedParam}`);
};

// Profile
export const useProfile = () => {
  return useApiQuery("dashboard/profile");
};

// Time cycles
export const useTimeCycles = () => {
  return useApiQuery("time-cycles");
};

// Permissions
export const usePermissions = () => {
  return useApiQuery("dashboard/permissions");
};

// Dues
export const useDues = (params) => {
  return useApiQuery(`dashboard/dues`, null, true, params);
};

export const useOwnerDues = (params, type) => {
  return useApiQuery(
    `dashboard/dues/owner?${new URLSearchParams(params).toString()}&type=${type}`
  );
};

export const useSharedMutation = (callbacks = {}) => {
  return useApiMutation(callbacks);
};
